import { useConfig } from "@/context/ConfigContext";
import { useEffect, useState } from "react";

/**
 * Hook untuk mengambil brands dari API
 * @param {Object} options - Options untuk fetch
 * @param {number} options.id - ID brand spesifik (optional)
 * @param {Object} options.params - Query parameters (optional)
 * @param {boolean} options.enabled - Enable/disable fetch (default: true)
 */
export function useBrands(options = {}) {
  const { id, params = {}, enabled = true } = options;
  const { config, configData } = useConfig();
  const [brands, setBrands] = useState([]);
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Pastikan config sudah ready dan team_id tersedia
    if (!enabled || !configData?.team_id || !config?.api?.baseUrl) {
      return;
    }

    const fetchBrands = async () => {
      try {
        setLoading(true);
        setError(null);

        const teamId = configData.team_id;

        // Build query string dari params
        const queryParams = new URLSearchParams();
        if (params.status) queryParams.append("status", params.status);
        if (params.page) queryParams.append("page", params.page);
        if (params.per_page) queryParams.append("per_page", params.per_page);
        if (params.search) queryParams.append("search", params.search);

        const queryString = queryParams.toString();

        // Gunakan proxy API route untuk menghindari CORS
        const proxyUrl = id
          ? `/api/products?team_id=${teamId}&type=brands&id=${id}${
              queryString ? `&${queryString}` : ""
            }`
          : `/api/products?team_id=${teamId}&type=brands${
              queryString ? `&${queryString}` : ""
            }`;

        // console.log("[useBrands] Fetching brands:", {
        //   proxyUrl,
        //   teamId,
        //   id,
        //   params,
        //   baseUrl: config?.api?.baseUrl,
        // });

        const response = await fetch(proxyUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          cache: "no-cache",
        });

        if (!response.ok) {
          let errorData;
          let errorText;

          try {
            errorText = await response.text();
            errorData = JSON.parse(errorText);
          } catch (parseError) {
            errorData = {
              message: errorText || response.statusText,
              status: response.status,
            };
          }

          const apiUrl = errorData.apiUrl || "N/A";

          const errorDetails = {
            proxyEndpoint: proxyUrl,
            laravelApiUrl: apiUrl,
            status: response.status,
            statusText: response.statusText,
            teamId: teamId,
            params: params,
            apiBaseUrl: config?.api?.baseUrl,
            errorMessage:
              errorData.message ||
              errorData.error ||
              errorText ||
              response.statusText,
            errorDetail: errorData.errorDetail || errorData.errorData,
            errorData: errorData,
          };

          console.error("[useBrands] API Error:", errorDetails);

          throw new Error(
            `API Error\n` +
              `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
              `Proxy Endpoint: ${proxyUrl}\n` +
              `Laravel API URL: ${apiUrl}\n` +
              `Status: ${response.status} ${response.statusText}\n` +
              `Team ID: ${teamId}\n` +
              `Params: ${JSON.stringify(params)}\n` +
              `API Base URL: ${config?.api?.baseUrl}\n` +
              `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
              `Error Message: ${
                errorData.message ||
                errorData.error ||
                errorText ||
                response.statusText
              }\n` +
              `Error Detail: ${
                errorData.errorDetail
                  ? errorData.errorDetail
                  : JSON.stringify(errorData, null, 2)
              }`,
          );
        }

        const data = await response.json();

        // Handle response berdasarkan apakah ada id atau tidak
        if (id) {
          // Single brand
          setBrand(data.data || data);
        } else {
          // List brands
          setBrands(data.data || data || []);
        }
      } catch (err) {
        console.error("[useBrands] Error fetching brands:", {
          message: err.message,
          stack: err.stack,
          teamId: configData?.team_id,
          params: params,
          baseUrl: config?.api?.baseUrl,
        });
        setError(err.message || "Gagal mengambil brands");
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, [
    configData?.team_id,
    config?.api?.baseUrl,
    id,
    enabled,
    JSON.stringify(params),
  ]);

  return {
    brands: id ? null : brands,
    brand: id ? brand : null,
    loading,
    error,
  };
}
